import { useEffect } from "react";
import { useAuthState } from "@/zustand/AuthState";
import { isUserAuthenticated } from "@/middleware/auth";
import { reconcileStrandedSends } from "@/middleware/strandedSendReconciliation";

/**
 * Module-level (per page-load) guard. Reconciliation must run at most ONCE per
 * boot: a mid-session re-run would race live in-flight sends and wrongly FAIL
 * them. A per-component ref does NOT deliver this — if the App subtree remounts
 * (an error-boundary reset, HMR) the ref resets and the sweep re-runs. A module
 * flag is torn down only when the JS context is (a real reload) — exactly the
 * "once per boot" scope we want.
 */
let hasReconciled = false;

/** Test-only: reset the once-per-boot guard between cases. */
export function __resetStrandedReconciliationGuard(): void {
  hasReconciled = false;
}

/**
 * Runs the stranded-send reconciliation ONCE per boot, after auth is FULLY
 * established. App.tsx mounts this on every route (login/guest included), so it
 * self-gates on auth.
 *
 * Gates on BOTH `tk` and `dk`, and depends on both. `dk` (the master key) is NOT
 * persisted and is restored ASYNCHRONOUSLY on reload (useAuthSession → unsealDk)
 * WITHOUT changing `tk`. Depending on `tk` alone, the effect fired once at mount
 * while `dk` was still empty, failed the isUserAuthenticated() gate, and never
 * re-ran when `dk` landed — so on the primary "reopen while logged in" path the
 * whole sweep silently no-op'd. Depending on `dk` too re-fires the effect when
 * the key is restored, and isUserAuthenticated() then passes.
 */
export function useStrandedSendReconciliation(): void {
  const token = useAuthState((s) => s.tk);
  const derivedKey = useAuthState((s) => s.dk);

  useEffect(() => {
    if (hasReconciled || !token || !derivedKey || !isUserAuthenticated()) {
      return;
    }
    hasReconciled = true;
    void reconcileStrandedSends().catch(() => undefined);
  }, [token, derivedKey]);
}
